/**
 * URL state encode/decode (v4).
 * Maps ConfigState <-> URL query params.
 *
 * Wire format (short keys):
 *   ?f=f-3p&t=satin-forest&g=leg-brass&y=35&p=18&z=1
 *
 * Keys: f = formeId, t = fabricId (tissu ; sur un produit a `materials` c'est le coloris), m = materialId
 * (matiere, produits a `materials` seulement, 2026-09-07), g = legId, y = yaw, p = pitch, z = zoom
 */

import type { ConfigState } from "@/types";

const KEY_MAP = {
  formeId: "f",
  fabricId: "t",
  materialId: "m",
  legId: "g",
  yaw: "y",
  pitch: "p",
  zoom: "z",
} as const;

export function encodeState(state: ConfigState): URLSearchParams {
  const params = new URLSearchParams();
  params.set(KEY_MAP.formeId, state.formeId);
  params.set(KEY_MAP.fabricId, state.fabricId);
  if (state.materialId) params.set(KEY_MAP.materialId, state.materialId);
  params.set(KEY_MAP.legId, state.legId);
  params.set(KEY_MAP.yaw, roundTo(state.yaw, 1).toString());
  params.set(KEY_MAP.pitch, roundTo(state.pitch, 1).toString());
  params.set(KEY_MAP.zoom, roundTo(state.zoom, 2).toString());
  return params;
}

export function decodeState(search: string, defaults: ConfigState): ConfigState {
  const params = new URLSearchParams(search);
  // ?layout=<formeId> is a long-form alias for ?f=<formeId>, used by the Quaternius
  // living-room product so URLs like ?layout=medium read naturally.
  const formeId = params.get("layout") ?? params.get(KEY_MAP.formeId) ?? defaults.formeId;
  return {
    formeId,
    fabricId: params.get(KEY_MAP.fabricId) ?? defaults.fabricId,
    materialId: params.get(KEY_MAP.materialId) ?? defaults.materialId,
    legId: params.get(KEY_MAP.legId) ?? defaults.legId,
    yaw: parseFloatOr(params.get(KEY_MAP.yaw), defaults.yaw),
    pitch: parseFloatOr(params.get(KEY_MAP.pitch), defaults.pitch),
    zoom: parseFloatOr(params.get(KEY_MAP.zoom), defaults.zoom),
  };
}

function parseFloatOr(value: string | null, fallback: number): number {
  if (value === null) return fallback;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

function roundTo(n: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

/** Keys not owned by ConfigState that we preserve when rewriting the URL. Critically this
 *  includes the EMBED keys: dropping `nvslug` here was making the live-price lookup
 *  (currentSlug) go null after the first URL rewrite, freezing the price; and dropping the
 *  embed/theme keys would reset the PDP mode + host look on any iframe reload. */
const PRESERVED_KEYS = new Set([
  "force3d",
  "forcefallback",
  "config",
  "debug",
  "product",
  "theme",
  "forcemobile",
  "nvslug",   // the configurator slug — currentSlug() needs it for live price + checkout
  "embed",    // PDP/self-contained embed mode
  "cta",      // cta=self
  "bg", "fg", "font", "radius", "accent", // host-theme adaptation params
]);

/** Update window.location without page reload. */
export function pushURL(state: ConfigState): void {
  if (typeof window === "undefined") return;
  const current = new URLSearchParams(window.location.search);
  const params = encodeState(state);
  for (const key of PRESERVED_KEYS) {
    const value = current.get(key);
    if (value !== null) params.set(key, value);
  }
  const newURL = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, "", newURL);
}

/** Build a full shareable URL for the current state. */
export function shareURL(state: ConfigState): string {
  if (typeof window === "undefined") return "";
  const params = encodeState(state);
  return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
}
